import { Stack } from '@mui/material';
import moment from 'moment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Typography } from 'melp-design/components';
import { useConfirm } from 'store/confirm';
import {
  UnlinkAppUsersStatus,
  useUnlinkAppUsers,
  useUnlinkAppUsersStatus,
} from 'store/admin-clients';

interface Props {
  clientId: string;
}

const runningStatuses = new Set(['active', 'waiting', 'delayed', 'paused']);

const isUnlinkRunning = (status: UnlinkAppUsersStatus['status'] | undefined) =>
  !!status && runningStatuses.has(status);

const statusColor = (status: UnlinkAppUsersStatus['status']) => {
  if (status === 'failed' || status === 'stuck') return 'error';
  if (status === 'completed') return 'success';
  return 'textSecondary';
};

export const UnlinkAppUsers: FC<Props> = ({ clientId }) => {
  const { t } = useTranslation();
  const { confirmation } = useConfirm();
  const {
    data: status,
    refetch,
    isLoading,
    isFetching,
  } = useUnlinkAppUsersStatus(clientId);
  const { mutate: unlinkAppUsers, isLoading: isStarting } = useUnlinkAppUsers();
  const [isChecking, setIsChecking] = useState(false);

  const isRunning = isUnlinkRunning(status?.status);
  const processed = status?.progress?.processed ?? 0;
  const total = status?.progress?.total ?? 0;
  const failed = status?.progress?.failed ?? 0;

  const statusLabel = (() => {
    if (isLoading) {
      return t('clients.auth.unlinkAppUsers.loading', {
        defaultValue: 'Loading status...',
      });
    }

    if (!status?.status) {
      return t('clients.auth.unlinkAppUsers.none', {
        defaultValue: 'No unlink job yet.',
      });
    }

    switch (status.status) {
      case 'waiting':
      case 'delayed':
      case 'paused':
        return t('clients.auth.unlinkAppUsers.queued', {
          defaultValue: 'Queued.',
        });
      case 'active':
        return failed > 0
          ? t('clients.auth.unlinkAppUsers.activeFailed', {
              processed,
              total,
              failed,
              defaultValue:
                'In progress. Removed {{processed}} of {{total}}. {{failed}} failed.',
            })
          : t('clients.auth.unlinkAppUsers.active', {
              processed,
              total,
              defaultValue: 'In progress. Removed {{processed}} of {{total}}.',
            });
      case 'completed':
        return total === 0
          ? t('clients.auth.unlinkAppUsers.completedEmpty', {
              defaultValue: 'No linked app users.',
            })
          : t('clients.auth.unlinkAppUsers.completed', {
              processed,
              defaultValue: 'Removed {{processed}} app users.',
            });
      case 'failed':
      case 'stuck':
        return t('clients.auth.unlinkAppUsers.failed', {
          processed,
          total,
          failed,
          defaultValue:
            'Failed. Removed {{processed}} of {{total}}. {{failed}} failed.',
        });
      default:
        return status.status;
    }
  })();

  return (
    <Stack
      gap={1.5}
      sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}
    >
      <Typography variant="h3">
        {t('clients.auth.unlinkAppUsers.title', {
          defaultValue: 'Unlink app users',
        })}
      </Typography>
      <Typography variant="p2" color="textSecondary">
        {t('clients.auth.unlinkAppUsers.description', {
          defaultValue:
            'Removes the app login for every employee of this client and deletes the Cognito user. Logins shared with another client are removed too. Employees are linked again the next time they sign in.',
        })}
      </Typography>
      <Stack direction="row" gap={1}>
        <Button
          type="button"
          variant="danger-outline"
          label={t('clients.auth.unlinkAppUsers.action', {
            defaultValue: 'Unlink app users',
          })}
          loading={isStarting || isChecking}
          disabled={isLoading || isRunning || isStarting}
          onClick={async () => {
            const { confirmed } = await confirmation(
              t('clients.auth.unlinkAppUsers.confirm', {
                defaultValue:
                  'This removes app logins for employees of this client and deletes their Cognito users.',
              }),
              t('clients.auth.unlinkAppUsers.confirmTitle', {
                defaultValue: 'Unlink app users?',
              }),
              'danger',
            );

            if (!confirmed) return;

            setIsChecking(true);
            try {
              const latest = await refetch();
              if (latest.isError) return;

              if (isUnlinkRunning(latest.data?.status)) {
                toast.info(
                  t('clients.auth.unlinkAppUsers.alreadyRunning', {
                    defaultValue: 'Unlink is already running for this client.',
                  }),
                );
                return;
              }

              unlinkAppUsers(
                { clientId },
                {
                  onSuccess: () => {
                    toast.success(
                      t('clients.auth.unlinkAppUsers.started', {
                        defaultValue:
                          'Unlink started. Refresh to see progress.',
                      }),
                    );
                  },
                },
              );
            } finally {
              setIsChecking(false);
            }
          }}
        />
        <Button
          type="button"
          variant="neutral-outline"
          label={t('clients.auth.unlinkAppUsers.refresh', {
            defaultValue: 'Refresh status',
          })}
          loading={isFetching}
          onClick={() => {
            void refetch();
          }}
        />
      </Stack>
      {status?.ranAt ? (
        <Typography variant="p2" color="textSecondary">
          {t('clients.auth.unlinkAppUsers.lastRun', {
            date: moment(status.ranAt).format('l LT'),
            defaultValue: 'Last run: {{date}}',
            interpolation: { escapeValue: false },
          })}
        </Typography>
      ) : null}
      <Typography variant="p2" color={statusColor(status?.status ?? null)}>
        {statusLabel}
      </Typography>
      {status?.error ? (
        <Typography variant="p2" color="error">
          {status.error}
        </Typography>
      ) : null}
    </Stack>
  );
};
